import type { ApplicationErrorBody, ApplicationErrorCode } from "../types/contract";

/**
 * A failure the server described in the typed contract (§10.5).
 *
 * The distinction that matters: an `ApiError` is a decision the server made and
 * explained, so the UI can act on `code`. Anything else — a dropped connection,
 * a proxy returning HTML — is a `TransportError`, which the UI can only report.
 * Collapsing the two would make "you are not allowed to do that" and "the
 * network blipped" look the same to the code that has to choose what to show.
 */
export class ApiError extends Error {
  readonly code: ApplicationErrorCode;
  readonly status: number;
  readonly details: Record<string, unknown> | undefined;
  readonly requestId: string | undefined;

  constructor(status: number, body: ApplicationErrorBody) {
    super(body.message);
    this.name = "ApiError";
    this.status = status;
    this.code = body.code;
    this.details = body.details;
    this.requestId = body.requestId;
  }

  /** §9.1 — someone else changed this resource while it was open. */
  get isConflict(): boolean {
    return this.code === "RESOURCE_CONFLICT" || this.code === "STALE_CANONICAL_REVISION";
  }

  /** The session is gone or was never there: the UI should offer sign-in. */
  get isUnauthenticated(): boolean {
    return this.code === "AUTH_REQUIRED";
  }
}

export class TransportError extends Error {
  override readonly cause: unknown;
  constructor(message: string, cause?: unknown) {
    super(message);
    this.name = "TransportError";
    this.cause = cause;
  }
}

const KNOWN_CODES = new Set<string>([
  "AUTH_REQUIRED", "FORBIDDEN", "PROJECT_NOT_FOUND", "RESOURCE_NOT_FOUND",
  "ENGINE_PROJECT_INCOMPATIBLE", "STALE_CANONICAL_REVISION", "RESOURCE_CONFLICT",
  "PLAN_INVALID", "VALIDATION_BLOCKING", "LIFECYCLE_GATE_FAILED", "GIT_PUBLISH_FAILED",
  "JOB_FAILED", "BAD_REQUEST", "CHANGE_SET_STATE_INVALID", "RATE_LIMITED", "INTERNAL_ERROR",
]);

/**
 * Turn a failed response body into an `ApiError`.
 *
 * An unrecognised code becomes `INTERNAL_ERROR` rather than being trusted: a
 * body that does not match the contract is not a contract the client can
 * reason about, whatever it calls itself.
 */
export function toApiError(status: number, payload: unknown): ApiError {
  const body = (typeof payload === "object" && payload !== null ? payload : {}) as Record<string, unknown>;
  const code = typeof body.code === "string" && KNOWN_CODES.has(body.code)
    ? (body.code as ApplicationErrorCode)
    : "INTERNAL_ERROR";
  const message = typeof body.message === "string" && body.message
    ? body.message
    : `The server returned ${status} without a contract error body.`;
  return new ApiError(status, {
    code,
    message,
    ...(typeof body.details === "object" && body.details !== null ? { details: body.details as Record<string, unknown> } : {}),
    ...(typeof body.requestId === "string" ? { requestId: body.requestId } : {}),
  });
}

/** What to put in front of a user, including the id that finds the server log. */
export function describeError(error: unknown): { title: string; detail: string; requestId?: string } {
  if (error instanceof ApiError) {
    return {
      title: TITLES[error.code],
      detail: error.message,
      ...(error.requestId ? { requestId: error.requestId } : {}),
    };
  }
  if (error instanceof TransportError) return { title: "Cannot reach the server", detail: error.message };
  return { title: "Unexpected failure", detail: error instanceof Error ? error.message : String(error) };
}

const TITLES: Record<ApplicationErrorCode, string> = {
  AUTH_REQUIRED: "Sign in required",
  FORBIDDEN: "Not permitted",
  PROJECT_NOT_FOUND: "Project not found",
  RESOURCE_NOT_FOUND: "Not found",
  ENGINE_PROJECT_INCOMPATIBLE: "Engine incompatible with this project",
  STALE_CANONICAL_REVISION: "The project moved on",
  RESOURCE_CONFLICT: "Someone else changed this",
  PLAN_INVALID: "The Engine refused the change",
  VALIDATION_BLOCKING: "Blocking validation findings",
  LIFECYCLE_GATE_FAILED: "Lifecycle gate not satisfied",
  GIT_PUBLISH_FAILED: "Publication failed",
  JOB_FAILED: "The job failed",
  BAD_REQUEST: "The request was malformed",
  CHANGE_SET_STATE_INVALID: "Not valid in this state",
  RATE_LIMITED: "Too many requests",
  INTERNAL_ERROR: "Server error",
};
